<template>
    <spinner-until :condition="!showSpinner">
        <div class="survey-note-edit mt-page">

            <b-row class="mb-3 align-items-md-center">
                <b-col cols="12" md="4">
                    <b-badge variant="warning"
                             v-if="!draftSurveyNote.locked">
                        {{ $i18n.gt('BADGE_DRAFT') }}
                    </b-badge>
                    <h1>
                        {{ $i18n.gt('HEADING_EDIT') }} {{
                            $i18n.gt(`LABEL_SURVEY_NOTE_TYPE_${draftSurveyNote.note_type}`)
                        }} {{
                            $i18n.gt('HEADING_NOTE')
                        }}
                    </h1>
                </b-col>
                <b-col cols="12" md="4">
                    <patient-id :participant="participant" class="mb-3 mb-md-0">
                    </patient-id>
                </b-col>
                <b-col cols="12" md="4" class="text-md-right">
                    <b-btn class="btn-delete"
                           v-if="$auth.canDeleteDraftSoapNote(draftSurveyNote.note_type)"
                           @click="deleteNote()">{{ $i18n.gt('LABEL_DELETE_NOTE') }}
                    </b-btn>
                    <b-btn variant="primary" class="ml-3"
                           @click="preview()">{{ $i18n.gt('LABEL_PREVIEW_SAVE') }}
                    </b-btn>
                </b-col>
            </b-row>

            <survey-note-form :participantId="participantId">
            </survey-note-form>

            <!-- preview modal -->

            <b-modal no-close-on-backdrop
                     size="lg"
                     id="previewNoteModal"
                     title="Preview Provider Note">
                <div class="survey-note-form__preview">
                    <survey-note-view-practitioner :survey-note="draftSurveyNote">
                    </survey-note-view-practitioner>
                </div>

                <template v-slot:modal-footer>
                    <b-alert show variant="info" class="mr-5">
                        <shs-icon name="warning"></shs-icon>
                        {{ $i18n.gt('LABEL_NOTE_LOCK_WARNING') }}
                    </b-alert>
                    <b-btn variant="warning"
                           @click="saveNote(false)">
                        {{ $i18n.gt('LABEL_SAVE_DRAFT') }}
                    </b-btn>
                    <b-btn class="btn btn-primary"
                           v-if="$auth.canLockSoapNote(draftSurveyNote)"
                           @click="saveNote(true)">
                        {{ $i18n.gt('LABEL_LOCK_NOTE') }}
                    </b-btn>
                </template>

            </b-modal>

        </div>
    </spinner-until>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>

import Vue from 'vue'
import * as _ from 'lodash'
import PractitionerPatientMixin from '@/providers/PractitionerPatientMixin'
import SurveyNote from '@/providers/SurveyNote'
import Utils from "@/providers/Utils";

/**
 * Page for Practitioner to create/edit a SurveyNote
 */
export default Vue.extend({
    name: 'SurveyNoteEdit',
    mixins: [PractitionerPatientMixin],
    props: {
        participantId: Number, // for PractitionerPatientMixin
        surveyNoteId: Number,  // not required when creating a note
        noteType: String,      // not required when editing an existing note
        practiceId: Number,    // only used when routing via RPM
    },
    computed: {
        showSpinner() {
            // must have these things loaded to guarantee that note initialization will work
            return !this.$store.state.patientParticipant.latestScoreDefs
        },
        heading() {
            if (this.participant && this.participant.fullname) {
                return this.$i18n.gt('HEADING_PATIENT_CHART_FOR').replace('{name}', this.participant.fullname)
            } else {
                return ''
            }
        },
        draftSurveyNote() {
            return this.$store.state.draftSurveyNote
        },
        // shortcut for watch/autosave purposes
        ccmAttributes() {
            if (this.draftSurveyNote) {
                return {
                    review_minutes: this.draftSurveyNote.review_minutes,
                    ccm_review_minutes: this.draftSurveyNote.ccm_review_minutes,
                    ccm_demographics: this.draftSurveyNote.ccm_demographics,
                    ccm_problem_list: this.draftSurveyNote.ccm_problem_list,
                    ccm_symptom_management: this.draftSurveyNote.ccm_symptom_management,
                    ccm_medication_use: this.draftSurveyNote.ccm_medication_use,
                    ccm_allergies: this.draftSurveyNote.ccm_allergies,
                    ccm_clinical_summary: this.draftSurveyNote.ccm_clinical_summary,
                    ccm_goals: this.draftSurveyNote.ccm_goals,
                    cancer_screening_due: this.draftSurveyNote.cancer_screening_due,
                    cancer_screening_types: this.draftSurveyNote.cancer_screening_types,
                    isCcm: this.draftSurveyNote.isCcm,
                }
            } else {
                return {}
            }
        }
    },
    methods: {
        preview() {
            this.$bvModal.show('previewNoteModal')
        },
        async deleteNote() {
            const ok = await this.$bvModal.msgBoxConfirm(
                'Permanently delete this draft note?', {
                    title: 'Delete Provider Note',
                    okTitle: 'Delete Note',
                    okVariant: 'danger',
                    cancelTitle: 'Cancel',
                    cancelVariant: 'cancel'
                })
            if (ok) {
                try {
                    await this.$api.deleteItem(this, 'survey-note', this.draftSurveyNote)

                    // reload participant and notes, etc.
                    this.$store.dispatch('getPatientParticipant', {
                        participantId: this.participantId,
                        force: true
                    })
                    this.$toasted.show(this.$i18n.gt('MESSAGE_NOTE_DELETED'), {duration: 3000})
                } catch (e) {
                    this.$utils.handleError(e, 'Could not delete note')
                }

                if (this.practiceId) {
                    this.$router.replace(`/rpm/${this.practiceId}`)
                } else {
                    this.$router.replace(`/participant/chart/id/${this.participantId}`)
                }
            }
        },
        async saveNote(lock) {
            try {
                // set non-persistent save type flag
                this.draftSurveyNote.save_type = (lock ? 'lock' : 'draft')
                this.draftSurveyNote.locking_practitioner_id = this.$user.practitionerId()
                await this.$api.updateItem(this, 'survey-note', this.draftSurveyNote, false)

                this.$bvModal.hide('previewNoteModal')
                // reload participant and notes, etc.
                this.$store.dispatch('getPatientParticipant', {
                    participantId: this.participantId,
                    force: true
                })
                const msgKey = lock ? 'MESSAGE_NOTE_LOCKED' : 'MESSAGE_NOTE_SAVED_AS_DRAFT'
                this.$toasted.show(this.$i18n.gt(msgKey), {duration: 3000})
            } catch (e) {
                this.$utils.handleError(e, 'Could not save note')
            }

            // Route back to RPM page when practice id is present
            if (this.practiceId) {
                this.$router.replace(`/rpm/${this.practiceId}`)
            } else {
                this.$router.replace(`/participant/chart/id/${this.participantId}`)
            }
        },
    },
    async mounted() {
        try {
            await this.$store.dispatch('getParticipantScores', {participantId: this.participantId})
            // TODO: ensure participant loaded before invoking this (how? - participant loading is triggered in PatientMixin)
            await this.loadParticipant(true) //This works but new data shows up a moment after old data
            const surveyNote = await SurveyNote.loadOrCreateSurveyNote(this, this.surveyNoteId, this.participantId, this.noteType) // see SurveyNote.js
            if (!this.surveyNoteId) {
                // if creating a note, redirect to edit page
                if (this.practiceId) {
                    this.$router.replace(
                        `/rpm/${this.practiceId}/editNote/participantId/${this.participantId}/id/${surveyNote.id}`
                    )
                } else {
                    this.$router.replace(
                        `/participant/editNote/participantId/${this.participantId}/id/${surveyNote.id}`
                    )
                }
            }
        } catch (e) {
            this.$router.replace(`/participant/chart/id/${this.participantId}`)
            this.$utils.handleError(e, 'Could not create Note')
        }
    },

    watch: {
        // watch for input changes from various components displayed on SurveyNoteForm

        // triggered by ReportWidgetGoal
        'draftSurveyNote.plan_goals': {
            deep: true,
            handler: function (newVal, oldVal) {
                if (newVal) {
                    SurveyNote.autosave(this)
                }
            }
        },
        // triggered by ReportWidgetResources
        'draftSurveyNote.plan_options': {
            deep: true,
            handler: function (newVal, oldVal) {
                if (newVal) {
                    SurveyNote.autosave(this)
                }
            }
        },
        // triggered by SurveyNoteFormHandouts
        'draftSurveyNote.handouts': {
            deep: true,
            handler: function (newVal, oldVal) {
                if (newVal) {
                    SurveyNote.autosave(this)
                }
            }
        },
        // triggered by SurveyNoteFormCcm (see computed var above)
        'ccmAttributes': {
            deep: true,
            handler: function (newVal, oldVal) {
                if (newVal) {
                    SurveyNote.autosave(this)
                }
            }
        },
        // triggered by SurveyNoteForm
        'draftSurveyNote.text.entered': {
            deep: true,
            handler: function (newVal, oldVal) {
                SurveyNote.autosave(this)
            }
        },
        'draftSurveyNote.text.includeGenerated': {
            deep: true,
            handler: function (newVal, oldVal) {
                SurveyNote.autosave(this)
            }
        }
    }

})
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">

.survey-note-edit {
}

</style>
